import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CustomPagination from '../../../components/forms/Pagination';
import { theme } from '../../../theme';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, Skeleton, SvgIcon, TableSortLabel, Typography } from '@mui/material';
import { PagedData_1OfWasteHeaders, WasteHeaders } from '../../../app/services/api/generated';
import { useNavigate } from 'react-router-dom';
import { formatDateHourString } from '../../../utils';
import { Trash01 } from '../../../assets';
import { SetStateAction, Dispatch, useContext } from 'react';
import RoundAndFormatValue from '../../../components/shared/RoundAndFormatValue';
import { WasteChecksheetTab } from '../enums/WasteChecksheetTab';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import { Pill } from '../../../components/shared/Pill';
import { ConfirmationDeleteDialog } from '../../../components/shared/dialogs/ConfirmationDeleteDialog';
import { UserContext } from '../../../components/shared/useUser';

export default function WasteChecksheetTable(props: {
  readonly list: PagedData_1OfWasteHeaders,
  readonly activeTab: WasteChecksheetTab;
  readonly currencyCode?: string,
  readonly page: number,
  readonly setPage: Dispatch<SetStateAction<number>>,
  readonly order: 'asc' | 'desc',
  readonly setOrder: Dispatch<SetStateAction<'asc' | 'desc'>>,
  readonly orderBy: string,
  readonly setOrderBy: Dispatch<SetStateAction<string>>,
  readonly date: string,
  readonly clearAll: () => void;
  readonly setDeleteId: Dispatch<SetStateAction<number>>;
  readonly setOpenModal: Dispatch<SetStateAction<boolean>>;
  readonly openModal: boolean;
  readonly handleConfirmDeleteModal: () => Promise<void>;
  readonly isLoading: boolean;
  readonly navigateToDailyWasteCheckSheet: () => void;
  readonly isCreating: boolean;
  readonly onAddIsDisabled: boolean;
}) {
  const { t } = useTranslation('wasteChecksheet');
  const navigate = useNavigate();

  const rowsPerPage = 10;
  const maxPages = props.list?.totalCount
    ? Math.max(1, Math.ceil(props.list.totalCount / rowsPerPage))
    : 0;

  //Header sort
  const handleSort = (property: keyof WasteHeaders) => {
    const isAsc = props.orderBy === property && props.order === 'asc';
    props.setPage(0);
    props.setOrder(isAsc ? 'desc' : 'asc');
    props.setOrderBy(property);
  };
  const { selectedStore } = useContext(UserContext);

  const deleteButtonHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: number) => {
    event.stopPropagation();
    props.setDeleteId(id);
    props.setOpenModal(true);
  };

  const handleCancelModal = () => {
    props.setOpenModal(false);
  };

  if (props.list.data?.length == 0) {
    return <EmptyTable
      text={t('title')}
      searchTerm={props.date}
      onClear={props.clearAll}
      onAdd={props.navigateToDailyWasteCheckSheet}
      onAddIsCreating={props.isCreating}
      addItemText={t('createNewBtn')}
      onAddIsDisabled={props.onAddIsDisabled} />;
  }

  return (
    <>
      <Box sx={{ p: 5 }}>
        <Typography variant='textMD'
          fontWeight={600}
          sx={{ display: 'flex', alignItems: 'center' }}>
          {t('title')}
          {props.list?.totalCount && <Pill text={`${props.list?.totalCount} Checksheets`}></Pill>}
        </Typography>
      </Box>
      {props.isLoading ? <Skeleton sx={{ bgcolor: theme.palette.custom.gray[100] }}
        variant='rectangular'
        height={445.89} /> :
        <TableContainer component={'div'}>
          <Table>
            <TableHead sx={{ backgroundColor: theme.palette.custom.gray[200] }}>
              <TableRow>
                <TableCell>
                  {t('list.id')}
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={props.orderBy === 'createdDate'}
                    direction={props.order}
                    onClick={() => handleSort('createdDate')}
                  >
                    {t('list.createdDate')}
                  </TableSortLabel>
                </TableCell>
                {
                  props.activeTab === WasteChecksheetTab.ARCHIVED &&
                  <>
                    <TableCell>
                      <TableSortLabel
                        active={props.orderBy === 'submittedDate'}
                        direction={props.order}
                        onClick={() => handleSort('submittedDate')}
                      >
                        {t('list.submittedDate')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={props.orderBy === 'submittedBy'}
                        direction={props.order}
                        onClick={() => handleSort('submittedBy')}
                      >
                        {t('list.submittedBy')}
                      </TableSortLabel>
                    </TableCell></>
                }
                <TableCell align="right">
                  <TableSortLabel
                    active={props.orderBy === 'totalWaste'}
                    direction={props.order}
                    onClick={() => handleSort('totalWaste')}
                  >
                    {t('list.totalWaste')}
                  </TableSortLabel>
                </TableCell>
                <TableCell align='right'>
                  <TableSortLabel
                    active={props.orderBy === 'totalCost'}
                    direction={props.order}
                    onClick={() => handleSort('totalCost')}
                  >
                    {t('list.totalCost')}
                  </TableSortLabel>
                </TableCell>
                {props.activeTab === WasteChecksheetTab.ACTIVE &&
                  <TableCell align="center">
                  </TableCell>
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {props.list.data?.map((row) => (
                <TableRow key={row.id}
                  onClick={() => navigate(`/store/daily-waste-checksheet/${row.id}`)}
                  sx={{ cursor: 'pointer' }}>
                  <TableCell>
                    <Typography variant='textXS'
                      fontWeight={600}
                      color={theme.palette.primary[500]}
                      sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                      {row.id ?? ''}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='textXS' >
                      {row.createdDate ? formatDateHourString(row.createdDate, selectedStore?.franchiseName) : ''}
                    </Typography>
                  </TableCell>
                  {
                    props.activeTab === WasteChecksheetTab.ARCHIVED && (
                      <>
                        <TableCell>
                          {row.submittedDate ? formatDateHourString(row.submittedDate, selectedStore?.franchiseName) : ''}
                        </TableCell>
                        <TableCell>
                          {row.submittedBy}
                        </TableCell>
                      </>
                    )
                  }
                  <TableCell align="right">
                    {row.totalWaste}
                  </TableCell>
                  <TableCell align="right">
                    <RoundAndFormatValue value={row.totalCost ?? 0}
                      currencyCode={props.currencyCode} />
                  </TableCell>
                  {props.activeTab === WasteChecksheetTab.ACTIVE &&
                    <TableCell
                      align="center"
                      sx={{ p: 0, alignContent: 'center' }}
                    >
                      <IconButton
                        size='small'
                        onClick={(event) => deleteButtonHandler(event, row.id ?? -1)}>
                        <SvgIcon style={{ color: theme.palette.error[500] }}><Trash01></Trash01></SvgIcon>
                      </IconButton>
                    </TableCell>}
                </TableRow>
              ))}

              {/* Add empty rows to maintain height */}
              {Array.from({ length: Math.max(0, 10 - (props.list.data?.length ?? 0)) }).map((_, index) => (
                <TableRow key={`empty-row-${index}`}
                  sx={{ height: '40px', backgroundColor: theme.palette.common.white }}>
                  <TableCell colSpan={6}
                    sx={{
                      backgroundColor: theme.palette.common.white,
                      borderColor: index === Math.max(0, 10 - (props.list.data?.length ?? 0)) - 1 ? theme.palette.custom.gray[100] : theme.palette.common.white,
                    }} />
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>}
      <CustomPagination useNoDataMessage={false}
        page={props.page}
        setPage={props.setPage}
        maxPages={maxPages}
        breakpointForChangeDisplay={120000}
        disabled={props.isLoading} />
      <ConfirmationDeleteDialog open={props.openModal}
        onCancel={handleCancelModal}
        onConfirm={props.handleConfirmDeleteModal}
        nameOfObject={t('details.title')} />
    </>
  );
}


